import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { signInWithProvider, type OAuthProvider } from '../../lib/oauth';
import { Button, Spinner, Input, OAuthButton } from '../../components/design-system';

/** Login — Google or email/password, with the Sign in/Register toggle above it. */
export function Login() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingProvider, setPendingProvider] = useState<OAuthProvider | null>(null);

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (authError) throw authError;
      // Don't wait on the auth-state effect to route — if it's slow (or the
      // profile hasn't loaded yet) the button just sits on "Signing in…" and
      // the sign-in looks ignored. Navigate as soon as we have the session.
      if (data.session) {
        navigate('/', { replace: true });
        return;
      }
      setError('Sign in did not complete. Please try again.');
      setLoading(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
      setLoading(false);
    }
  }

  async function handleProvider(provider: OAuthProvider) {
    setError('');
    setPendingProvider(provider);
    try {
      await signInWithProvider(provider);
      // On success the page redirects (web) or a deep link completes auth
      // (native), so we intentionally leave the spinner running.
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
      setPendingProvider(null);
    }
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        <OAuthButton
          provider="google"
          tone="inverted"
          label="Sign in with Google"
          onClick={() => void handleProvider('google')}
          disabled={pendingProvider !== null}
          loading={pendingProvider === 'google'}
        />
        <OAuthButton
          provider="apple"
          tone="inverted"
          label="Sign in with Apple"
          onClick={() => void handleProvider('apple')}
          disabled={pendingProvider !== null}
          loading={pendingProvider === 'apple'}
        />
      </div>

      <div className="flex items-center gap-3 my-5">
        <span className="h-px flex-1" style={{ background: 'rgba(255, 252, 219, .2)' }} />
        <span className="text-xs uppercase tracking-wide" style={{ color: 'rgba(255, 252, 219, .5)' }}>or</span>
        <span className="h-px flex-1" style={{ background: 'rgba(255, 252, 219, .2)' }} />
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input
          id="email"
          type="email"
          label="Email"
          tone="inverted"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          autoComplete="email"
        />
        <Input
          id="password"
          type="password"
          label="Password"
          tone="inverted"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          required
          autoComplete="current-password"
        />
        <Link
          to="/forgot-password"
          className="text-sm text-right -mt-2 underline underline-offset-2 font-medium sk-hit"
          style={{ color: 'rgba(255, 252, 219, .7)' }}
        >
          Forgot password?
        </Link>
        {error && <p className="text-sm text-crimson">{error}</p>}
        <Button
          type="submit"
          size="xs"
          tone="inverted"
          fullWidth
          disabled={loading}
          className="mt-2"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" />
              Signing in…
            </span>
          ) : (
            'Sign in'
          )}
        </Button>
      </form>
    </>
  );
}

export default Login;
